import { useEffect, useMemo, useState } from "react";
import Header from "../../Header";
import LoadingSpinner from "../../Shared/LoadingSpinner";
import ReferenceDataAPI from "./ReferenceDataAPI";
import { RESISTANCE_GROUP_LIST } from "../SprayProgram/resistanceGroupList";
import "./ReferenceData.css";

// DATA: a read-only look at the tables the pricing and the spuit programs run on. Nothing on this screen can be changed.
// The data itself is changed the usual way: the CSV files and `python config.py init-database`.

const LABELS = {
  crop_name: "Crop", id: "Id", product_id: "Product id", product_name: "Product", product_pack_name: "Pack", AGI: "Code", unit_volume: "Unit size",
  full_pack_units: "Units per box", full_pack_volume: "Box size", list_price_pv: "List price R/L", season_price_pv: "Season price R/L",
  max_dosage: "Max dose /ha", tier1_min_rpha: "Tier 1 min R/ha", tier2_min_rpha: "Tier 2 min R/ha", tier2_min_dosage: "Tier 2 min dose /ha",
  tier3_min_invoice: "Tier 3 min invoice R", tier1_price_pv: "Tier 1 price R/L", tier2_price_pv: "Tier 2 price R/L",
  tier1_discount_pct: "Tier 1 %", tier2_discount_pct: "Tier 2 %", tier3_discount_pct: "Tier 3 %",
  resistance_price_pv: "Resistance price R/L", resistance_discount_pct: "Resistance %",
  pricing2_id: "Pack row id", role: "Role", group_no: "Group", product: "Product", dose_per_spray: "Dose per spray", sprays_per_season: "Sprays",
  min_cover: "Min cover", bundle_pack: "Bundle pack", bundle_resistance_price: "Bundle resistance price R/L",
  choice_id: "Choice id", category: "Category", target: "Target", dosage: "Dose /ha", is_parent: "Ander", resistance_group: "Resistance group",
  resistanceGroup: "Resistance group", name: "Product", unit: "Unit", packagingSizes: "Pack sizes", isParent: "Ander",
};

const SECTIONS = [
  {
    id: "pricing", label: "Prices", load: ReferenceDataAPI.getPricing,
    note: "Table pricing2: one row per pack and program. Tier thresholds, tier prices and the resistance price are on the same row. The lower of the tier price and the season price is what the order screens use (*).",
    source: "Source: pricing2, loaded from mypack_pricing.xlsx (pricing_update.py, then init-database).",
    preferred: ["crop_name", "id", "product_name", "product_pack_name", "AGI", "unit_volume", "full_pack_units", "full_pack_volume", "list_price_pv", "season_price_pv", "tier1_min_rpha", "tier1_price_pv", "tier2_min_rpha", "tier2_min_dosage", "tier2_price_pv", "tier3_min_invoice", "tier3_discount_pct", "max_dosage", "resistance_price_pv", "resistance_discount_pct"],
  },
  {
    id: "bundles", label: "Resistance bundles", load: ReferenceDataAPI.getBundles,
    note: "Table pricing2Dependants. A MAIN row is the product that earns the resistance price (Silvalis) with its own dose and sprays. PARTNER rows are the products that must be bought with it. Every group must be covered; inside a group any one product will do. Min cover 0,97 means a shortfall of 3% is allowed.",
    source: "Source: pricing2Dependants, loaded from config_csvs/pricing2Dependants.csv.",
    preferred: ["pricing2_id", "bundle_pack", "bundle_resistance_price", "role", "group_no", "product", "dose_per_spray", "sprays_per_season", "min_cover"],
  },
  {
    id: "choices", label: "Choices", load: ReferenceDataAPI.getChoices,
    note: "Table choices with the names filled in: the recommended product and dose for each crop, category and target. These are the pick-lists of the new spuit program.",
    source: "Source: choices, categories, productType, products, loaded from mypack_crop_targets.xlsx (crop_targets_update.py, then init-database).",
    preferred: ["crop_name", "category", "target", "product", "dosage", "resistance_group", "is_parent", "choice_id"],
  },
  {
    id: "products", label: "Products", load: ReferenceDataAPI.getProducts,
    note: "Table products: unit, pack sizes and the resistance group of each product.",
    source: "Source: products, loaded from config_csvs/products.csv.",
    preferred: ["id", "name", "unit", "packagingSizes", "resistanceGroup", "isParent"],
  },
  { id: "groups", label: "Resistance groups", load: ReferenceDataAPI.getProducts, note: "The resistance groups (IRAC / FRAC / HRAC) and the products that carry each one. A product without a group can be given one on its row in the new spuit program.", source: "Source: products.resistanceGroup; the pick-list is resistanceGroupList.js (the Resistance groups list of the SST sheet)." },
  { id: "rules", label: "Rules", note: "The rules the screens apply today, and where each value comes from. These are not editable here; a change to a rule is a change to the code or to the data files.", source: "" },
];

const RULES = [
  ["Resistance price (Silvalis)", "Every group of the bundle must be covered: Ampligo, Agrimec Gold, and Sorba or Denim Fit. Each partner must reach the cover below.", "pricing2Dependants (group_no); bundleCheck.js"],
  ["Chris's prototype rule", "Any 3 of the 4 companions (Agrimec, Ampligo, Sorba, Denim Fit) earn the resistance price. To be confirmed with Chris against the rule above.", "docs/mypack.md (prototype)"],
  ["Shortfall allowance", "Each partner needs min cover x the main product's hectares. Today 0,97 (3% shortfall) in the database. The prototype uses 2%. To be confirmed with Chris.", "pricing2Dependants.min_cover"],
  ["Resistance check never blocks", "The bundle check only sets the Silvalis price. The order can always be created.", "SprayProgram.js, HybridVolume.js"],
  ["Silvalis and tier discounts", "When the bundle is met the resistance price replaces the tier price on the whole volume. Otherwise Silvalis follows its own tier data.", "calcVolumes.js"],
  ["Tier 1 and Tier 2", "Decided by the list cost per ha of the order against the Tier 1 and Tier 2 minimums on each pack row. Tier 2 also needs the minimum dose per ha.", "pricing2.tier1_min_rpha, tier2_min_rpha, tier2_min_dosage"],
  ["Tier 3", "Needs Tier 1 or Tier 2 as well as the total list invoice minimum.", "pricing2.tier3_min_invoice; calcVolumes.js and services.py"],
  ["Maximum dose", "Only the volume up to max dose x hectares qualifies for a tier discount. 999 means no maximum.", "pricing2.max_dosage"],
  ["Season price", "If the price after discount is higher than the season price, the season price is used (shown with *).", "pricing2.season_price_pv"],
  ["Whole boxes", "The new spuit program adds up the litres of each pack for the whole farm and rounds up to whole boxes.", "programCalc.js"],
  ["Products without a price", "'Ander' and products that are not in the price list are listed in the order with their volume only: no boxes, no prices, not on the MyPack invoice.", "programCalc.js"],
];

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "Ja" : "";
  if (typeof v === "number") return v.toLocaleString("en-ZA", { maximumFractionDigits: 4 });
  return String(v);
};
const sortKey = (v) => (v === null || v === undefined ? "" : v);

const columnsFor = (rows, preferred = []) => {
  const keys = rows.length ? Object.keys(rows[0]) : [];
  const first = preferred.filter((k) => keys.includes(k));
  return [...first, ...keys.filter((k) => !first.includes(k))];
};

const toCsv = (cols, rows) => {
  const q = (v) => `"${String(v === null || v === undefined ? "" : v).replace(/"/g, '""')}"`;
  return [cols.map(q).join(","), ...rows.map((r) => cols.map((c) => q(r[c])).join(","))].join("\r\n");
};

const Grid = ({ rows, preferred, name }) => {
  const [search, setSearch] = useState("");
  const [crop, setCrop] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const cols = useMemo(() => columnsFor(rows, preferred), [rows, preferred]);
  const crops = useMemo(() => [...new Set(rows.map((r) => r.crop_name).filter(Boolean))], [rows]);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = rows.filter((r) => (!crop || r.crop_name === crop) && (!s || cols.some((c) => fmt(r[c]).toLowerCase().includes(s))));
    if (sort.key) {
      list = [...list].sort((a, b) => {
        const x = sortKey(a[sort.key]);
        const y = sortKey(b[sort.key]);
        return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "af", { numeric: true })) * sort.dir;
      });
    }
    return list;
  }, [rows, cols, search, crop, sort]);

  const download = () => {
    const blob = new Blob(["﻿" + toCsv(cols, shown)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${name}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <div className="rd-tools">
        <input type="search" placeholder="Soek ..." aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        {crops.length > 1 && (
          <select value={crop} onChange={(e) => setCrop(e.target.value)} aria-label="Crop">
            <option value="">All crops</option>
            {crops.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
        <span className="rd-count">
          {shown.length} of {rows.length} rows
        </span>
        <button type="button" onClick={download} disabled={!shown.length}>
          Download CSV
        </button>
      </div>
      <div className="rd-scroll">
        <table>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c} className={typeof (rows[0] || {})[c] === "number" ? "num" : ""} title={c} onClick={() => setSort((s) => ({ key: c, dir: s.key === c ? -s.dir : 1 }))}>
                  {LABELS[c] || c}
                  {sort.key === c ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c} className={typeof r[c] === "number" ? "num" : ""}>
                    {fmt(r[c])}
                  </td>
                ))}
              </tr>
            ))}
            {!shown.length && (
              <tr>
                <td colSpan={cols.length || 1} className="rd-empty">
                  No rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

const GroupsView = ({ rows }) => {
  const byGroup = new Map();
  rows.forEach((p) => {
    const g = (p.resistanceGroup || "").trim();
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g).push((p.name || "").trim());
  });
  const list = [...new Set([...RESISTANCE_GROUP_LIST, ...[...byGroup.keys()].filter(Boolean)])];
  const data = [
    ...list.map((g) => ({ group: g, products: (byGroup.get(g) || []).sort().join(", "), count: (byGroup.get(g) || []).length })),
    { group: "(no group)", products: (byGroup.get("") || []).sort().join(", "), count: (byGroup.get("") || []).length },
  ];
  return <Grid rows={data} preferred={["group", "count", "products"]} name="resistance_groups" />;
};

const ReferenceData = (props) => {
  const [tab, setTab] = useState("pricing");
  const [cache, setCache] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const section = SECTIONS.find((s) => s.id === tab);

  useEffect(() => {
    if (!section.load || cache[section.id]) return;
    let live = true;
    setBusy(true);
    setError("");
    section
      .load()
      .then((data) => live && setCache((c) => ({ ...c, [section.id]: data })))
      .catch((e) => live && setError(e.message))
      .finally(() => live && setBusy(false));
    return () => {
      live = false;
    };
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = cache[tab];
  return (
    <div className="rd-root" translate="no">
      <Header title="DATA" backPath="pastOrders" setPath={props.setPath} />
      <div className="rd-main">
        <h1>Data</h1>
        <p className="rd-lead">Read only. These are the tables the prices and the spuit programs run on.</p>
        <div className="rd-tabs" role="tablist">
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" role="tab" aria-selected={tab === s.id} className={tab === s.id ? "on" : ""} onClick={() => setTab(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
        <p className="rd-note">{section.note}</p>
        {section.source && <p className="rd-source">{section.source}</p>}
        {busy && <LoadingSpinner text="Laai ..." />}
        {error && <p className="rd-error">{error}</p>}
        {tab === "rules" && (
          <div className="rd-scroll">
            <table>
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>What the app does</th>
                  <th>Where it comes from</th>
                </tr>
              </thead>
              <tbody>
                {RULES.map(([a, b, c]) => (
                  <tr key={a}>
                    <td className="strong">{a}</td>
                    <td className="wrap">{b}</td>
                    <td className="wrap muted">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows && tab === "groups" && <GroupsView rows={rows} />}
        {rows && tab !== "groups" && tab !== "rules" && <Grid key={tab} rows={rows} preferred={section.preferred} name={tab} />}
      </div>
    </div>
  );
};

export default ReferenceData;
